import React, { Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';
import LoginForm from './components/auth/LoginForm';
import RegisterDoctor from './components/auth/RegisterDoctor';
import RegisterPatient from './components/auth/RegisterPatient';
import LandingPage from './components/landing/LandingPage';
import Dashboard from './components/dashboard/Dashboard';
import Navigation from './components/common/Navigation';
import DoctorsList from './components/doctors/DoctorsList';
import PatientsList from './components/patients/PatientsList';
import DiseasesList from './components/diseases/DiseasesList';
import StudiesList from './components/studies/StudiesList';
import HealthZonesList from './components/healthzones/HealthZonesList';
import HospitalsList from './components/hospitals/HospitalsList';
import ConsultationsList from './components/consultations/ConsultationsList';
import MedicamentsList from './components/medicaments/MedicamentsList';
import ActivitiesList from './components/activities/ActivitiesList';
import Library from './components/library/Library';
import ELearning from './components/elearning/ELearning';
import ChatSystem from './components/chat/ChatSystem';
import InteractiveMap from './components/map/InteractiveMap';
import UserProfile from './components/profile/UserProfile';

// Error Boundary pour capturer les erreurs React
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Une erreur est survenue</h2>
            <p className="text-gray-600 mb-4">L'application a rencontré un problème.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Recharger la page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

// Home Route Component - Landing page si non connecté, dashboard si connecté
const HomeRoute = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  return user ? <Navigate to="/dashboard" replace /> : <LandingPage />;
};

// Main Layout Component
const MainLayout = ({ children }) => {
  const { isCollapsed } = useSidebar();
  
  return (
    <div className="flex h-screen bg-gray-50">
      <Navigation />
      <div className={`flex-1 overflow-y-auto transition-all duration-300 ${
        isCollapsed ? 'ml-16' : 'ml-0 lg:ml-64'
      }`}>
        {children}
      </div>
    </div>
  );
};

function AppContent() {
  // Gérer les erreurs React pour éviter les crashes
  React.useEffect(() => {
    const handleError = (event) => {
      console.error('Global error caught:', event.error);
      // Ne pas propager l'erreur pour éviter le crash
      event.preventDefault();
    };
    
    window.addEventListener('error', handleError);
    
    return () => {
      window.removeEventListener('error', handleError);
    };
  }, []);

  return (
    <AuthProvider>
      <SidebarProvider>
        <Router>
          <div className="App">
            <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/login" element={<LoginForm />} />
            <Route path="/register-doctor" element={<RegisterDoctor />} />
            <Route path="/register-patient" element={<RegisterPatient />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Dashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/doctors"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <DoctorsList />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PatientsList />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/diseases"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <DiseasesList />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/studies"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <StudiesList />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/health-zones"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <HealthZonesList />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hospitals"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <HospitalsList />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/consultations"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ConsultationsList />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/medicaments"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <MedicamentsList />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/activities"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ActivitiesList />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Library />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/elearning"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ELearning />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ChatSystem />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/map"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <InteractiveMap />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <UserProfile />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            </Routes>
          </div>
        </Router>
      </SidebarProvider>
    </AuthProvider>
  );
}

// Wrapper de l'App avec ErrorBoundary
function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
}

export default App;
